/* eslint-disable import/no-cycle */
import { election as electionService } from '../../services';
import { ActionTypes as raceActionTypes } from './race';

export const ActionTypes = {
  SET_ELECTIONS: 'SET_ELECTIONS',
  SET_ALL_ELECTIONS: 'SET_ALL_ELECTIONS',
  SET_ALL_APPROVED_ELECTIONS: 'SET_ALL_APPROVED_ELECTIONS',
  SET_ALL_UNAPPROVED_ELECTIONS: 'SET_ALL_UNAPPROVED_ELECTIONS',
  CLEAR_ELECTIONS: 'CLEAR_ELECTIONS',
  SET_SELECTED_ELECTION: 'SET_SELECTED_ELECTION',
  SET_SELECTED_ELECTION_OBJ: 'SET_SELECTED_ELECTION_OBJ',
  CLEAR_SELECTED_ELECTION: 'CLEAR_SELECTED_ELECTION',
  API_ERROR: 'API_ERROR',
};

/**
 * @description action creator for getting elections by address
 * @param {String} address address
 * @param {Function} [successCallback = () => {}] function to call on success
 * @param {Function} [failureCallback = () => {}] function to call on failure
 */
export const getAllFromAddress = (address, successCallback = () => {}, failureCallback = () => {}) => {
  return async (dispatch, getState) => {
    try {
      const elections = await electionService.getAllFromAddress(address);
      dispatch({ type: ActionTypes.SET_ELECTIONS, payload: elections });

      // if only one election, immediately set it in the store
      if (elections.length === 1) {
        dispatch({ type: ActionTypes.SET_SELECTED_ELECTION, payload: elections[0]._id });

        dispatch({
          type: raceActionTypes.SET_RACES,
          payload: elections[0].races || [],
        });
      }

      successCallback(dispatch, getState());
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: ActionTypes.SET_ELECTIONS,
          error,
        },
      });

      failureCallback(error);
    }
  };
};

/**
 * @description action creator for getting all elections
 * @param {Function} [successCallback = () => {}] function to call on success
 * @param {Function} [failureCallback = () => {}] function to call on failure
 */
export const getAllElections = (successCallback = () => {}, failureCallback = () => {}) => {
  return async (dispatch) => {
    try {
      const elections = await electionService.fetchAllFutureElections();
      dispatch({ type: ActionTypes.SET_ALL_ELECTIONS, payload: elections });
      successCallback();
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: ActionTypes.SET_ALL_ELECTIONS,
          error,
        },
      });

      failureCallback(error);
    }
  };
};

/**
 * @description action creator for getting all unapproved elections
 * @param {Function} [successCallback = () => {}] function to call on success
 * @param {Function} [failureCallback = () => {}] function to call on failure
 */
export const getAllUnapprovedElections = (successCallback = () => {}, failureCallback = () => {}) => {
  return async (dispatch) => {
    try {
      const elections = await electionService.fetchAllUnapprovedFutureElections();
      dispatch({ type: ActionTypes.SET_ALL_UNAPPROVED_ELECTIONS, payload: elections });
      successCallback();
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: ActionTypes.SET_ALL_UNAPPROVED_ELECTIONS,
          error,
        },
      });

      failureCallback(error);
    }
  };
};

/**
 * @description action creator for getting all approved elections
 * @param {Function} [successCallback = () => {}] function to call on success
 * @param {Function} [failureCallback = () => {}] function to call on failure
 */
export const getAllApprovedElections = (successCallback = () => {}, failureCallback = () => {}) => {
  return async (dispatch) => {
    try {
      const elections = await electionService.fetchAllApprovedFutureElections();
      dispatch({ type: ActionTypes.SET_ALL_APPROVED_ELECTIONS, payload: elections });
      successCallback();
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: ActionTypes.SET_ALL_APPROVED_ELECTIONS,
          error,
        },
      });

      failureCallback(error);
    }
  };
};

/**
 * @description action creator for admin approving an election
 * @param {Function} [successCallback = () => {}] function to call on success
 * @param {Function} [failureCallback = () => {}] function to call on failure
 */
export const adminApproveElection = (electionId, successCallback = () => {}, failureCallback = () => {}) => {
  return async (dispatch) => {
    try {
      await electionService.adminApproveElection(electionId);

      const unApprovedElections = await electionService.fetchAllUnapprovedFutureElections();
      dispatch({ type: ActionTypes.SET_ALL_UNAPPROVED_ELECTIONS, payload: unApprovedElections });

      const approvedElections = await electionService.fetchAllApprovedFutureElections();
      dispatch({ type: ActionTypes.SET_ALL_APPROVED_ELECTIONS, payload: approvedElections });

      successCallback();
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: ActionTypes.SET_ALL_UNAPPROVED_ELECTIONS,
          error,
        },
      });

      failureCallback(error);
    }
  };
};

/**
 * @description action creator for admin approving an election
 * @param {Function} [successCallback = () => {}] function to call on success
 * @param {Function} [failureCallback = () => {}] function to call on failure
 */
export const adminUnapproveElection = (electionId, successCallback = () => {}, failureCallback = () => {}) => {
  return async (dispatch) => {
    try {
      await electionService.adminUnapproveElection(electionId);

      const unApprovedElections = await electionService.fetchAllUnapprovedFutureElections();
      dispatch({ type: ActionTypes.SET_ALL_UNAPPROVED_ELECTIONS, payload: unApprovedElections });

      const approvedElections = await electionService.fetchAllApprovedFutureElections();
      dispatch({ type: ActionTypes.SET_ALL_APPROVED_ELECTIONS, payload: approvedElections });

      successCallback();
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: ActionTypes.SET_ALL_UNAPPROVED_ELECTIONS,
          error,
        },
      });

      failureCallback(error);
    }
  };
};

/**
 * @description action creator for getting all elections
 * @param {Object} fields fields about election to create
 * @param {Function} [successCallback = () => {}] function to call on success
 * @param {Function} [failureCallback = () => {}] function to call on failure
 */
export const createElection = (fields, successCallback = () => {}, failureCallback = () => {}) => {
  return async (dispatch) => {
    try {
      await electionService.createElection(fields);
      const elections = await electionService.fetchAllFutureElections();
      dispatch({ type: ActionTypes.SET_ALL_ELECTIONS, payload: elections });
      successCallback();
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: ActionTypes.SET_ALL_ELECTIONS,
          error,
        },
      });

      failureCallback(error);
    }
  };
};

/**
 * @description action creator for getting elections by title
 * @param {String} title election title to search on
 * @param {Function} [successCallback = () => {}] function to call on success
 * @param {Function} [failureCallback = () => {}] function to call on failure
 */
export const searchElections = (title, successCallback, failureCallback) => {
  return async (dispatch) => {
    try {
      const elections = await electionService.searchElections(title);
      dispatch({ type: ActionTypes.SET_ELECTIONS, payload: elections });

      // if only one election, immediately set it in the store
      if (elections.length === 1) {
        dispatch({ type: ActionTypes.SET_SELECTED_ELECTION, payload: elections[0]._id });

        dispatch({
          type: raceActionTypes.SET_RACES,
          payload: elections[0].races || [],
        });
      }

      successCallback();
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: ActionTypes.SET_ELECTIONS,
          error,
        },
      });

      failureCallback(error);
    }
  };
};

/**
 * @description action creator for signing user out
 */
export const clearElections = () => {
  return (dispatch) => {
    dispatch({ type: ActionTypes.CLEAR_ELECTIONS });
  };
};

/**
 * @description action creator for selecting an election
 * @param {String} electionId the id of the election to select
 */
export const setSelectedElection = (electionId) => {
  return (dispatch, getState) => {
    // set the election object
    dispatch({ type: ActionTypes.SET_SELECTED_ELECTION, payload: electionId });

    // set the races if possible
    const selectedElection = getState().elections.all_elections.find(e => e._id === electionId);

    dispatch({
      type: selectedElection ? raceActionTypes.SET_RACES : raceActionTypes.CLEAR_RACES,
      payload: selectedElection ? selectedElection.races : [],
    });
  };
};

/**
 * @description action creator for clearing selected election
 */
export const clearSelectedElection = () => {
  return (dispatch) => {
    dispatch({ type: ActionTypes.CLEAR_SELECTED_ELECTION });
    dispatch({ type: raceActionTypes.CLEAR_RACES });
  };
};
